import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Undo2, Package, Plus, ScanBarcode, Upload, FileText, Loader2 } from 'lucide-react';
import { PurchaseReturnHistory } from './PurchaseReturnHistory';
import { PurchaseReturnViewDialog } from './PurchaseReturnViewDialog';
import { PRItemsTable } from './PRItemsTable';

var API = process.env.REACT_APP_BACKEND_URL + '/api';
var RETURN_TYPES = ['Replacement', 'Credit Note', 'Refund'];

export default function PurchaseReturns() {
  var [searchParams] = useSearchParams();
  var grnIdFromUrl = searchParams.get('grnId');
  var [activeTab, setActiveTab] = useState(grnIdFromUrl ? 'create' : 'history');
  var [returns, setReturns] = useState([]);
  var [stats, setStats] = useState(null);
  var [loading, setLoading] = useState(true);
  var [grns, setGrns] = useState([]);
  var [selectedGrnId, setSelectedGrnId] = useState(grnIdFromUrl || '');
  var [grnDetails, setGrnDetails] = useState(null);
  var [returnType, setReturnType] = useState('Credit Note');
  var [items, setItems] = useState([]);
  var [notes, setNotes] = useState('');
  var [submitting, setSubmitting] = useState(false);
  var [debitNoteUrl, setDebitNoteUrl] = useState('');
  var [invoiceUrl, setInvoiceUrl] = useState('');
  var [ewayUrl, setEwayUrl] = useState('');
  var [uploading, setUploading] = useState('');
  var [scanBarcode, setScanBarcode] = useState('');
  var scannerRef = useRef(null);
  var [viewPR, setViewPR] = useState(null);
  var [viewOpen, setViewOpen] = useState(false);
  var [filterStatus, setFilterStatus] = useState('all');
  var [editingPrId, setEditingPrId] = useState(null);

  var fetchData = useCallback(function() {
    setLoading(true);
    var token = localStorage.getItem('token');
    var headers = { Authorization: 'Bearer ' + token };
    Promise.all([
      axios.get(API + '/purchase-returns', { headers: headers }),
      axios.get(API + '/purchase-returns/stats/summary', { headers: headers }),
      axios.get(API + '/grn?limit=200', { headers: headers })
    ]).then(function(res) {
      setReturns(res[0].data); setStats(res[1].data); setGrns(res[2].data.filter(function(g) { return g.status === 'completed'; }));
      setLoading(false);
    }).catch(function() { toast.error('Failed to load data'); setLoading(false); });
  }, []);

  useEffect(function() { fetchData(); }, [fetchData]);
  useEffect(function() { if (selectedGrnId) fetchGrnItems(selectedGrnId); }, [selectedGrnId]);

  function fetchGrnItems(grnId) {
    var token = localStorage.getItem('token');
    axios.get(API + '/purchase-returns/grn/' + grnId + '/items', { headers: { Authorization: 'Bearer ' + token } })
      .then(function(res) { setGrnDetails(res.data); setItems(res.data.items.map(function(i) { return Object.assign({}, i, { selected: false, return_quantity: 0, reason: 'Damaged', reason_notes: '' }); })); })
      .catch(function() { toast.error('Failed to fetch GRN items'); });
  }

  function toggleItem(idx) { var n = items.slice(); n[idx] = Object.assign({}, n[idx], { selected: !n[idx].selected }); if (!n[idx].selected) n[idx].return_quantity = 0; setItems(n); }
  function updateItem(idx, field, value) { var n = items.slice(); n[idx] = Object.assign({}, n[idx]); n[idx][field] = value; setItems(n); }

  function handleFileUpload(e, type) {
    var file = e.target.files[0]; if (!file) return;
    if (['application/pdf','image/jpeg','image/jpg','image/png'].indexOf(file.type) < 0) { toast.error('Invalid file'); return; }
    setUploading(type);
    var token = localStorage.getItem('token');
    var fd = new FormData(); fd.append('file', file);
    axios.post(API + '/grn/upload-invoice', fd, { headers: { Authorization: 'Bearer ' + token, 'Content-Type': 'multipart/form-data' } })
      .then(function(res) { if (type === 'debit_note') setDebitNoteUrl(res.data.url); else if (type === 'invoice') setInvoiceUrl(res.data.url); else setEwayUrl(res.data.url); toast.success('Uploaded'); setUploading(''); })
      .catch(function() { toast.error('Upload failed'); setUploading(''); });
  }

  function handleBarcodeScan() {
    var code = scanBarcode.trim(); if (!code) return;
    var idx = items.findIndex(function(i) { return i.barcode === code || i.product_sku === code; });
    if (idx >= 0) { var n = items.slice(); n[idx] = Object.assign({}, n[idx], { selected: true, return_quantity: Math.min(1, n[idx].available_stock) }); setItems(n); toast.success(items[idx].product_name + ' selected'); }
    else toast.error('Product not found in this GRN');
    setScanBarcode(''); if (scannerRef.current) scannerRef.current.focus();
  }

  function handleSubmit(finalize) {
    var sel = items.filter(function(i) { return i.selected && i.return_quantity > 0; });
    if (sel.length === 0) { toast.error('Select at least one item'); return; }
    setSubmitting(true);
    var token = localStorage.getItem('token');
    var payload = {
      grn_id: grnDetails.grn_id, grn_number: grnDetails.grn_number, supplier_id: grnDetails.supplier_id, supplier_name: grnDetails.supplier_name, return_type: returnType,
      items: sel.map(function(i) { return { product_id: i.product_id, product_name: i.product_name, product_sku: i.product_sku, return_quantity: parseFloat(i.return_quantity), available_quantity: i.available_stock, batch_number: i.batch_number || null, expiry_date: i.expiry_date || null, mfg_date: i.mfg_date || null, reason: i.reason, reason_notes: i.reason_notes || null, b2b_price_excl_gst: i.b2b_price_excl_gst, gst_percentage: i.gst_percentage, b2b_price_incl_gst: i.b2b_price_incl_gst }; }),
      notes: notes || null, debit_note_file_url: debitNoteUrl || null, invoice_file_url: invoiceUrl || null, eway_bill_file_url: ewayUrl || null
    };
    var headers = { Authorization: 'Bearer ' + token };
    var req;
    if (editingPrId) {
      // Edit existing draft via PUT (no finalize support — user must hit Finalize from history)
      req = axios.put(API + '/purchase-returns/' + editingPrId, payload, { headers: headers });
    } else {
      req = axios.post(API + '/purchase-returns?finalize=' + (finalize ? 'true' : 'false'), payload, { headers: headers });
    }
    req
      .then(function(res) {
        var msg = editingPrId ? 'Draft updated' : (finalize ? 'Purchase Return finalized — stock deducted' : 'Draft saved');
        toast.success(msg + (res.data.prn_number ? ' (' + res.data.prn_number + ')' : ''));
        setSubmitting(false); setActiveTab('history'); fetchData();
        setSelectedGrnId(''); setGrnDetails(null); setItems([]); setNotes('');
        setDebitNoteUrl(''); setInvoiceUrl(''); setEwayUrl(''); setEditingPrId(null);
      })
      .catch(function(err) { toast.error(err.response && err.response.data ? err.response.data.detail : 'Failed'); setSubmitting(false); });
  }

  function handleEditDraft(pr) {
    // Load a draft PR back into the create form for editing.
    setEditingPrId(pr.id);
    setSelectedGrnId(pr.grn_id);
    setGrnDetails({ grn_id: pr.grn_id, grn_number: pr.grn_number, supplier_id: pr.supplier_id, supplier_name: pr.supplier_name, invoice_number: pr.invoice_number });
    setReturnType(pr.return_type);
    setNotes(pr.notes || '');
    setDebitNoteUrl(pr.debit_note_file_url || '');
    setInvoiceUrl(pr.invoice_file_url || '');
    setEwayUrl(pr.eway_bill_file_url || '');
    // Fetch GRN items to get availability + map onto draft selections
    var token = localStorage.getItem('token');
    axios.get(API + '/purchase-returns/grn/' + pr.grn_id + '/items', { headers: { Authorization: 'Bearer ' + token } })
      .then(function(res) {
        var grnItems = res.data.items || [];
        var draftMap = {};
        pr.items.forEach(function(d) { draftMap[d.product_id] = d; });
        var merged = grnItems.map(function(g) {
          var d = draftMap[g.product_id];
          return Object.assign({}, g, {
            selected: !!d,
            return_quantity: d ? d.return_quantity : 0,
            reason: d ? d.reason : 'Damaged',
            reason_notes: d ? d.reason_notes : '',
            batch_number: d ? d.batch_number : g.batch_number,
            expiry_date: d ? d.expiry_date : g.expiry_date,
            mfg_date: d ? d.mfg_date : g.mfg_date,
            b2b_price_excl_gst: d ? d.b2b_price_excl_gst : g.b2b_price_excl_gst,
            gst_percentage: d ? d.gst_percentage : g.gst_percentage,
            b2b_price_incl_gst: d ? d.b2b_price_incl_gst : g.b2b_price_incl_gst,
          });
        });
        setItems(merged);
        setActiveTab('create');
        toast.info('Editing draft ' + pr.prn_number);
      })
      .catch(function() { toast.error('Failed to load GRN items'); });
  }

  function handleFinalizeDraft(id) {
    var t = localStorage.getItem('token');
    axios.post(API + '/purchase-returns/' + id + '/finalize', {}, { headers: { Authorization: 'Bearer ' + t } })
      .then(function() { toast.success('Finalized — stock deducted'); fetchData(); })
      .catch(function(e) { toast.error(e.response && e.response.data ? e.response.data.detail : 'Failed'); });
  }

  function handleDownloadPdf(id, prnNumber) {
    var t = localStorage.getItem('token');
    axios.get(API + '/purchase-returns/' + id + '/pdf', { headers: { Authorization: 'Bearer ' + t }, responseType: 'blob' })
      .then(function(res) {
        var url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        var a = document.createElement('a');
        a.href = url; a.download = 'PRN_' + (prnNumber || id) + '.pdf';
        document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(function() { toast.error('Failed to download PDF'); });
  }

  var selectedCount = 0, totalReturnQty = 0, totalReturnValue = 0;
  for (var k = 0; k < items.length; k++) { if (items[k].selected) { selectedCount++; totalReturnQty += (parseFloat(items[k].return_quantity) || 0); totalReturnValue += (parseFloat(items[k].return_quantity) || 0) * (items[k].b2b_price_incl_gst || 0); } }

  return React.createElement('div', { className: 'space-y-6', 'data-testid': 'purchase-returns-page' },
    React.createElement(PRHeader, null),
    stats && React.createElement(PRStats, { stats: stats }),
    React.createElement(Tabs, { value: activeTab, onValueChange: setActiveTab },
      React.createElement(TabsList, { className: 'grid w-full max-w-md grid-cols-2' },
        React.createElement(TabsTrigger, { value: 'create', className: 'gap-2', 'data-testid': 'tab-create-return' }, React.createElement(Plus, { className: 'w-4 h-4' }), 'Create Return'),
        React.createElement(TabsTrigger, { value: 'history', className: 'gap-2', 'data-testid': 'tab-return-history' }, React.createElement(FileText, { className: 'w-4 h-4' }), 'Return History')),
      React.createElement(TabsContent, { value: 'create', className: 'space-y-4 mt-4' },
        React.createElement(GRNSelectCard, { grns: grns, selectedGrnId: selectedGrnId, setSelectedGrnId: setSelectedGrnId, grnDetails: grnDetails, returnType: returnType, setReturnType: setReturnType }),
        grnDetails && items.length > 0 && React.createElement(ItemsCard, { items: items, toggleItem: toggleItem, updateItem: updateItem, scanBarcode: scanBarcode, setScanBarcode: setScanBarcode, scannerRef: scannerRef, handleBarcodeScan: handleBarcodeScan, selectedCount: selectedCount, totalReturnQty: totalReturnQty, totalReturnValue: totalReturnValue }),
        grnDetails && React.createElement(DocsCard, { notes: notes, setNotes: setNotes, uploading: uploading, handleFileUpload: handleFileUpload, debitNoteUrl: debitNoteUrl, invoiceUrl: invoiceUrl, ewayUrl: ewayUrl, submitting: submitting, handleSubmit: handleSubmit, selectedCount: selectedCount, editingPrId: editingPrId })),
      React.createElement(TabsContent, { value: 'history', className: 'mt-4' },
        React.createElement(PurchaseReturnHistory, { returns: returns, loading: loading, filterStatus: filterStatus, setFilterStatus: setFilterStatus, onView: function(pr) { setViewPR(pr); setViewOpen(true); }, onEdit: handleEditDraft, onFinalize: handleFinalizeDraft, onDownloadPdf: handleDownloadPdf }))),
    React.createElement(PurchaseReturnViewDialog, { pr: viewPR, open: viewOpen, onOpenChange: setViewOpen }));
}

function PRHeader() {
  return React.createElement('div', { className: 'flex items-center gap-3' },
    React.createElement(Undo2, { className: 'w-6 h-6 text-red-600' }),
    React.createElement('div', null,
      React.createElement('h1', { className: 'text-2xl font-bold text-slate-800' }, 'Purchase Returns'),
      React.createElement('p', { className: 'text-sm text-slate-500' }, 'Manage returns to suppliers from GRN')));
}

function PRStats({ stats }) {
  return React.createElement('div', { className: 'grid grid-cols-2 md:grid-cols-5 gap-3' },
    React.createElement(Card, null, React.createElement(CardContent, { className: 'pt-4 pb-3' }, React.createElement('p', { className: 'text-xs text-gray-500 uppercase' }, 'Total'), React.createElement('p', { className: 'text-2xl font-bold' }, stats.total))),
    React.createElement(Card, { className: 'border-amber-200 bg-amber-50' }, React.createElement(CardContent, { className: 'pt-4 pb-3' }, React.createElement('p', { className: 'text-xs text-amber-600 uppercase' }, 'Pending'), React.createElement('p', { className: 'text-2xl font-bold text-amber-700' }, stats.pending))),
    React.createElement(Card, { className: 'border-green-200 bg-green-50' }, React.createElement(CardContent, { className: 'pt-4 pb-3' }, React.createElement('p', { className: 'text-xs text-green-600 uppercase' }, 'Approved'), React.createElement('p', { className: 'text-2xl font-bold text-green-700' }, stats.approved))),
    React.createElement(Card, { className: 'border-red-200 bg-red-50' }, React.createElement(CardContent, { className: 'pt-4 pb-3' }, React.createElement('p', { className: 'text-xs text-red-600 uppercase' }, 'Rejected'), React.createElement('p', { className: 'text-2xl font-bold text-red-700' }, stats.rejected))),
    React.createElement(Card, { className: 'border-indigo-200 bg-indigo-50' }, React.createElement(CardContent, { className: 'pt-4 pb-3' }, React.createElement('p', { className: 'text-xs text-indigo-600 uppercase' }, 'Return Value'), React.createElement('p', { className: 'text-xl font-bold text-indigo-700' }, '₹' + (stats.total_approved_value || 0).toLocaleString('en-IN')))));
}

function GRNSelectCard({ grns, selectedGrnId, setSelectedGrnId, grnDetails, returnType, setReturnType }) {
  return React.createElement(Card, null,
    React.createElement(CardHeader, { className: 'pb-3' }, React.createElement(CardTitle, { className: 'text-lg flex items-center gap-2' }, React.createElement(Package, { className: 'w-5 h-5 text-blue-600' }), 'Select GRN')),
    React.createElement(CardContent, null,
      React.createElement('div', { className: 'grid grid-cols-1 md:grid-cols-3 gap-4' },
        React.createElement('div', null,
          React.createElement(Label, null, 'GRN Number *'),
          React.createElement(Select, { value: selectedGrnId, onValueChange: setSelectedGrnId },
            React.createElement(SelectTrigger, { className: 'mt-1', 'data-testid': 'select-grn' }, React.createElement(SelectValue, { placeholder: 'Select a completed GRN...' })),
            React.createElement(SelectContent, null, grns.map(function(g) { return React.createElement(SelectItem, { key: g.id, value: g.id }, g.grn_number + ' — ' + g.supplier_name); })))),
        grnDetails && React.createElement('div', null, React.createElement(Label, { className: 'text-gray-500' }, 'Supplier'), React.createElement('p', { className: 'mt-1 font-medium' }, grnDetails.supplier_name)),
        grnDetails && React.createElement('div', null, React.createElement(Label, { className: 'text-gray-500' }, 'Invoice #'), React.createElement('p', { className: 'mt-1 font-medium' }, grnDetails.invoice_number || '-'))),
      grns.length === 0 && React.createElement('div', { className: 'mt-3 p-4 bg-amber-50 border border-amber-200 rounded-lg' },
        React.createElement('p', { className: 'text-sm text-amber-800 font-medium' }, 'No completed GRNs found'),
        React.createElement('p', { className: 'text-xs text-amber-600 mt-1' }, 'Create and complete a GRN first from GRN Dashboard.')),
      grnDetails && React.createElement('div', { className: 'mt-4' },
        React.createElement(Label, null, 'Return Type *'),
        React.createElement(Select, { value: returnType, onValueChange: setReturnType },
          React.createElement(SelectTrigger, { className: 'mt-1 w-60', 'data-testid': 'select-return-type' }, React.createElement(SelectValue, null)),
          React.createElement(SelectContent, null, RETURN_TYPES.map(function(t) { return React.createElement(SelectItem, { key: t, value: t }, t); }))))));
}

function ItemsCard({ items, toggleItem, updateItem, scanBarcode, setScanBarcode, scannerRef, handleBarcodeScan, selectedCount, totalReturnQty, totalReturnValue }) {
  return React.createElement(Card, null,
    React.createElement(CardHeader, { className: 'pb-3' }, React.createElement(CardTitle, { className: 'text-lg flex items-center justify-between' }, React.createElement('span', { className: 'flex items-center gap-2' }, React.createElement(Package, { className: 'w-5 h-5 text-orange-600' }), 'Select Items'), React.createElement(Badge, null, selectedCount + ' selected'))),
    React.createElement(CardContent, { className: 'space-y-3' },
      React.createElement('div', { className: 'p-3 bg-slate-50 rounded-lg border' },
        React.createElement('div', { className: 'flex items-center gap-2 mb-2' }, React.createElement(ScanBarcode, { className: 'w-4 h-4 text-emerald-600' }), React.createElement('span', { className: 'text-sm font-medium' }, 'Barcode Scanner')),
        React.createElement('div', { className: 'flex gap-2' },
          React.createElement('div', { className: 'relative flex-1' }, React.createElement(ScanBarcode, { className: 'absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400' }), React.createElement(Input, { ref: scannerRef, value: scanBarcode, onChange: function(e) { setScanBarcode(e.target.value); }, onKeyDown: function(e) { if (e.key === 'Enter') handleBarcodeScan(); }, placeholder: 'Scan barcode...', className: 'pl-10 font-mono', 'data-testid': 'pr-barcode-input' })),
          React.createElement(Button, { onClick: handleBarcodeScan, disabled: !scanBarcode.trim(), className: 'bg-emerald-600 hover:bg-emerald-700 gap-1' }, React.createElement(ScanBarcode, { className: 'w-4 h-4' }), 'Find'))),
      React.createElement(PRItemsTable, { items: items, toggleItem: toggleItem, updateItem: updateItem }),
      React.createElement('div', { className: 'flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200' },
        React.createElement('div', { className: 'flex gap-6 text-sm' }, React.createElement('span', null, 'Items: ', React.createElement('strong', null, selectedCount)), React.createElement('span', null, 'Qty: ', React.createElement('strong', null, totalReturnQty))),
        React.createElement('span', { className: 'text-lg font-bold text-red-700' }, '₹' + totalReturnValue.toFixed(2)))));
}

function DocsCard({ notes, setNotes, uploading, handleFileUpload, debitNoteUrl, invoiceUrl, ewayUrl, submitting, handleSubmit, selectedCount, editingPrId }) {
  var docs = [{ label: 'Debit Note', type: 'debit_note', url: debitNoteUrl }, { label: 'Invoice', type: 'invoice', url: invoiceUrl }, { label: 'E-Way Bill', type: 'eway_bill', url: ewayUrl }];
  return React.createElement(Card, null,
    React.createElement(CardHeader, { className: 'pb-3' }, React.createElement(CardTitle, { className: 'text-lg flex items-center gap-2' }, React.createElement(Upload, { className: 'w-5 h-5 text-purple-600' }), 'Documents & Notes')),
    React.createElement(CardContent, null,
      React.createElement('div', { className: 'grid grid-cols-1 md:grid-cols-3 gap-4 mb-4' },
        docs.map(function(doc) {
          return React.createElement('div', { key: doc.type },
            React.createElement(Label, null, doc.label),
            React.createElement('div', { className: 'mt-1 flex items-center gap-2' },
              React.createElement('label', { className: 'flex items-center gap-2 px-4 py-2 bg-white border rounded-md cursor-pointer hover:bg-gray-50 text-sm' },
                React.createElement(Upload, { className: 'w-4 h-4' }),
                uploading === doc.type ? 'Uploading...' : (doc.url ? 'Uploaded' : 'Choose File'),
                React.createElement('input', { type: 'file', accept: '.pdf,.jpg,.jpeg,.png', onChange: function(e) { handleFileUpload(e, doc.type); }, className: 'hidden' })),
              doc.url && React.createElement(Badge, { className: 'bg-green-100 text-green-700' }, 'OK')));
        })),
      React.createElement('div', null, React.createElement(Label, null, 'Notes'), React.createElement(Textarea, { value: notes, onChange: function(e) { setNotes(e.target.value); }, placeholder: 'Additional remarks...', className: 'mt-1', rows: 2 })),
      React.createElement('div', { className: 'flex justify-end mt-4 gap-2' },
        editingPrId
          ? React.createElement(Button, {
              onClick: function() { handleSubmit(false); },
              disabled: submitting || selectedCount === 0,
              className: 'gap-2 bg-blue-600 hover:bg-blue-700',
              'data-testid': 'save-draft-edit-btn'
            },
              submitting ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' }) : React.createElement(FileText, { className: 'w-4 h-4' }),
              submitting ? 'Saving...' : 'Update Draft')
          : React.createElement(React.Fragment, null,
              React.createElement(Button, {
                variant: 'outline',
                onClick: function() { handleSubmit(false); },
                disabled: submitting || selectedCount === 0,
                className: 'gap-2 border-slate-400 text-slate-700 hover:bg-slate-100',
                'data-testid': 'save-draft-btn'
              },
                submitting ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' }) : React.createElement(FileText, { className: 'w-4 h-4' }),
                'Save as Draft'),
              React.createElement(Button, {
                onClick: function() { handleSubmit(true); },
                disabled: submitting || selectedCount === 0,
                className: 'gap-2 bg-red-600 hover:bg-red-700',
                'data-testid': 'save-finalize-btn'
              },
                submitting ? React.createElement(Loader2, { className: 'w-4 h-4 animate-spin' }) : React.createElement(Undo2, { className: 'w-4 h-4' }),
                submitting ? 'Saving...' : 'Save & Finalize')))));
}
